import * as React from 'react';

import { cn } from '../../lib/utils';
import { LoginCard } from '../../molecules/LoginCard';
import { SocialLoginButton, type SocialProvider } from '../../molecules/SocialLoginButton';

export interface LoginFormProps {
  /** Callback when the form is submitted with email */
  onEmailSubmit?: (email: string) => void;
  /** Callback when a social login button is clicked */
  onSocialLogin?: (provider: SocialProvider) => void;
  /** Whether the form is in loading state */
  loading?: boolean;
  /** Disables the email input + continue button (social login stays active) */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Title text (default: "Sign in") */
  title?: string;
  /** Email input placeholder (default: "Email") */
  emailPlaceholder?: string;
  /** Continue button text (default: "Continue") */
  continueButtonText?: string;
  /** Social providers to display (default: ['google']) */
  socialProviders?: SocialProvider[];
  /** Custom content to render in the social login section (e.g., actual OAuth buttons) */
  customSocialContent?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * LoginForm Organism
 *
 * Complete login form containing:
 * - "Sign In" title
 * - Email input
 * - Continue button (orange, #FF5722)
 * - "Or" divider
 * - Social login buttons
 *
 * @example
 * ```tsx
 * <LoginForm
 *   onEmailSubmit={(email) => handleLogin(email)}
 *   onSocialLogin={(provider) => handleSocialLogin(provider)}
 * />
 * ```
 */
export const LoginForm = React.forwardRef<HTMLDivElement, LoginFormProps>(
  (
    {
      onEmailSubmit,
      onSocialLogin,
      loading = false,
      disabled = false,
      error,
      title = 'Sign in',
      emailPlaceholder = 'Email',
      continueButtonText = 'Continue',
      socialProviders = ['google'],
      customSocialContent,
      className,
    },
    ref
  ) => {
    const [email, setEmail] = React.useState('');
    const [hasError, setHasError] = React.useState(false);
    const emailInputRef = React.useRef<HTMLInputElement>(null);

    const handleSubmit = React.useCallback(() => {
      if (disabled) return;
      if (!email || !email.includes('@')) {
        setHasError(true);
        return;
      }
      setHasError(false);
      onEmailSubmit?.(email);
    }, [disabled, email, onEmailSubmit]);

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleSubmit();
        }
      },
      [handleSubmit]
    );

    const handleEmailChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (hasError) setHasError(false);
      },
      [hasError]
    );

    const displayError = error || hasError;

    return (
      <LoginCard ref={ref} className={className}>
        {/* Sign in title */}
        <p className="font-outfit font-medium text-[16px] leading-[1.2] text-dawn-black capitalize">
          {title}
        </p>

        {/* Email + Continue button container */}
        <div className="flex flex-col gap-5 items-center w-full">
          {/* Email input with error message */}
          <div className="flex flex-col gap-2 items-start w-[322px]">
            <div
              className={cn(
                'w-full h-10 rounded border border-solid flex items-center px-2',
                'transition-colors duration-200',
                displayError
                  ? 'border-error'
                  : 'border-grey-400 focus-within:border-dawn-black focus-within:bg-orange-100'
              )}
            >
              <input
                ref={emailInputRef}
                type="email"
                autoFocus
                value={email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                placeholder={emailPlaceholder}
                disabled={loading || disabled}
                className={cn(
                  'w-full font-outfit font-normal text-[13px] leading-[1.3] text-dawn-black',
                  'placeholder:text-grey-400 focus:outline-none bg-transparent',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              />
            </div>
            {displayError && (
              <p className="font-outfit text-[10px] font-normal capitalize leading-[1.2] text-error">
                {error || 'Please enter a valid email address'}
              </p>
            )}
          </div>

          {/* Continue button */}
          <button
            onClick={handleSubmit}
            disabled={loading || disabled}
            className={cn(
              'w-[322px] h-10 bg-dawn-orange rounded flex items-center justify-center',
              'hover:bg-dawn-orange-hover transition-colors duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {loading ? (
              <span className="animate-spin w-[18px] h-[18px] border-2 border-dawn-white border-t-transparent rounded-full" />
            ) : (
              <span className="font-outfit font-medium text-[14px] leading-[1.2] text-dawn-white capitalize">
                {continueButtonText}
              </span>
            )}
          </button>
        </div>

        {/* Or section - only show if there are social providers or custom content */}
        {(socialProviders.length > 0 || customSocialContent) && (
          <div className="flex flex-col gap-3 items-center w-full">
            {/* Or text */}
            <p className="font-outfit font-normal text-[13px] leading-[1.3] text-dawn-black text-center w-full">
              Or
            </p>

            {/* Social login buttons */}
            {socialProviders.map((provider) => (
              <SocialLoginButton
                key={provider}
                provider={provider}
                onClick={() => onSocialLogin?.(provider)}
                loading={loading}
                className="w-[322px]"
              />
            ))}

            {/* Custom social content (e.g., actual OAuth buttons) */}
            {customSocialContent}
          </div>
        )}
      </LoginCard>
    );
  }
);

LoginForm.displayName = 'LoginForm';

export default LoginForm;
